import { createContext, useContext, useState, useEffect, useCallback } from 'react';

const ThemeContext = createContext();

// Three themes: light, evening (blue), dark (black)
const THEMES = ['light', 'evening', 'dark'];

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [theme, setThemeState] = useState(() => {
    // Check localStorage for saved preference, default to 'light'
    if (typeof window !== 'undefined') {
      return localStorage.getItem('wantd-theme') || 'light';
    }
    return 'light';
  });
  
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    // Check if user is logged in
    if (typeof window !== 'undefined') {
      return !!localStorage.getItem('token');
    }
    return false;
  });

  // Listen for auth changes
  useEffect(() => {
    const checkAuth = () => {
      const hasToken = !!localStorage.getItem('token');
      setIsAuthenticated(hasToken);
      
      // Force light mode when signed out
      if (!hasToken && theme !== 'light') {
        setThemeState('light');
      }
    };
    
    // Check on mount
    checkAuth();
    
    // Listen for storage changes (login/logout in other tabs)
    window.addEventListener('storage', checkAuth);
    
    // Custom event for same-tab auth changes
    window.addEventListener('authChange', checkAuth);
    
    return () => {
      window.removeEventListener('storage', checkAuth);
      window.removeEventListener('authChange', checkAuth);
    };
  }, [theme]);

  useEffect(() => {
    // Apply theme to document
    document.documentElement.setAttribute('data-theme', theme);
    // Save to localStorage
    localStorage.setItem('wantd-theme', theme);

    // Update the theme-color meta tag so Android Chrome's browser chrome
    // and the PWA titlebar colour matches the active theme.
    const themeColorMap = {
      light:   '#1E3A5F', // navy — matches the light nav bar
      evening: '#1E3A5F', // same navy nav
      dark:    '#0F172A', // near-black for dark theme
    };
    const metaThemeColor = document.querySelector('meta[name="theme-color"]');
    if (metaThemeColor) {
      metaThemeColor.setAttribute('content', themeColorMap[theme] ?? '#1E3A5F');
    }
  }, [theme]);

  const setTheme = useCallback((newTheme) => {
    // Only allow theme changes when authenticated
    if (!isAuthenticated && newTheme !== 'light') {
      return; // Ignore non-light theme changes when signed out
    }
    setThemeState(newTheme);
  }, [isAuthenticated]);

  const toggleTheme = useCallback(() => {
    // Only allow theme toggle when authenticated
    if (!isAuthenticated) {
      return;
    }
    
    // Cycle through: light -> evening -> dark -> light
    setThemeState(prev => {
      const currentIndex = THEMES.indexOf(prev);
      const nextIndex = (currentIndex + 1) % THEMES.length;
      return THEMES[nextIndex];
    });
  }, [isAuthenticated]);

  // Notify theme context when auth changes
  const notifyAuthChange = useCallback(() => {
    window.dispatchEvent(new Event('authChange'));
  }, []);

  const value = {
    theme,
    setTheme,
    toggleTheme,
    isDark: theme === 'dark' || theme === 'evening',
    isEvening: theme === 'evening',
    isBlackDark: theme === 'dark',
    themes: THEMES,
    isAuthenticated,
    notifyAuthChange,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};
